/**
 * MultiPaperDetector — detect additional exam papers beyond the primary tracked paper.
 *
 * Strategy: coarse 8×5 grid scan of the full video frame using the same
 * ArUco-style marker patterns as the primary paper. Grid positions that
 * fall inside the primary paper's bounding box are skipped. Any marker
 * hits outside the primary region are clustered by proximity; a cluster
 * of ≥ 2 distinct hits signals a secondary paper.
 *
 * Designed to run at ~2 Hz (every 500 ms) via setInterval. One offscreen
 * canvas is shared with the primary marker detection cycle — the caller
 * must ensure the canvas was rendered with the current video frame before
 * calling detectMultiPaper.
 *
 * AI Proctor 6원칙 #2 — evidence-first: secondaryCount and confidence are
 * both surfaced so reviewers can judge, not a single boolean flag.
 */

import { detectAnyMarkerAt } from './MarkerDetector';
import type { Point2D } from './Homography';

// ── Public types ─────────────────────────────────────────

export interface MultiPaperResult {
  /** Number of secondary (non-primary) paper instances detected. 0 = single paper only. */
  secondaryCount: number;
  /** Approximate normalised centre positions of detected secondary papers. */
  secondaryCenters: Point2D[];
  /** 0..1 confidence that secondaryCount > 0 is genuine. */
  confidence: number;
  narratorText: string;
  uiColor: 'green' | 'yellow' | 'orange' | 'red';
}

const NONE: MultiPaperResult = {
  secondaryCount: 0,
  secondaryCenters: [],
  confidence: 0,
  narratorText: '추가 시험지 없음',
  uiColor: 'green',
};

// ── Public API ───────────────────────────────────────────

/**
 * Detect secondary papers by scanning a coarse grid outside the primary
 * paper's bounding box.
 *
 * @param video        Source video element
 * @param primaryCorners  Current primary paper corners (normalised 0–1, order TL/TR/BR/BL)
 * @param offCanvas    Reusable offscreen canvas (same as used for primary detection)
 * @param roiSize      ROI half-size in pixels (default: 40 — same as primary)
 */
export function detectMultiPaper(
  video: HTMLVideoElement,
  primaryCorners: Point2D[],
  offCanvas: HTMLCanvasElement,
  roiSize = 40,
): MultiPaperResult {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return NONE;

  // Ensure canvas matches video size and draw current frame
  if (offCanvas.width !== vw || offCanvas.height !== vh) {
    offCanvas.width = vw;
    offCanvas.height = vh;
  }
  const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return NONE;
  ctx.drawImage(video, 0, 0, vw, vh);

  // Primary paper bounding box (with 10% margin so edge markers don't re-fire)
  const pBbox = cornersBbox(primaryCorners, 0.10);

  // 8×5 = 40 scan positions across the frame
  const COLS = 8;
  const ROWS = 5;

  type Hit = { nx: number; ny: number; markerId: number; confidence: number };
  const hits: Hit[] = [];

  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const nx = (col + 0.5) / COLS;
      const ny = (row + 0.5) / ROWS;

      // Skip positions inside primary paper's bounding box
      if (inBbox(nx, ny, pBbox)) continue;

      const hit = detectAnyMarkerAt(ctx, vw, vh, nx, ny, roiSize);
      if (hit) hits.push(hit);
    }
  }

  if (hits.length === 0) return NONE;

  // Cluster hits by proximity (normalised distance < 0.25)
  const clusters = clusterHits(hits, 0.25);

  // Require ≥ 2 hits per cluster for a credible secondary paper signal
  const strong = clusters.filter(c => c.hits.length >= 2);

  if (strong.length === 0) {
    // Single weak hit — very low confidence, worth noting
    return {
      secondaryCount: 0,
      secondaryCenters: [],
      confidence: 0.2,
      narratorText: '추가 마커 흔적 (단일 신호, 미확정)',
      uiColor: 'yellow',
    };
  }

  const secondaryCenters = strong.map(c => ({ x: c.cx, y: c.cy }));
  const avgConf = strong.reduce((s, c) => s + c.avgConf, 0) / strong.length;
  // Confidence: average pattern match quality × min(1, hitCount/4)
  const hitBonus = Math.min(1, strong.reduce((s, c) => s + c.hits.length, 0) / 4);
  const confidence = Math.min(1, avgConf * hitBonus);

  const count = strong.length;
  const narratorText =
    count === 1 ? `추가 시험지 1매 감지 (신뢰도 ${(confidence * 100).toFixed(0)}%)`
    : `추가 시험지 ${count}매 감지 (신뢰도 ${(confidence * 100).toFixed(0)}%)`;

  return {
    secondaryCount: count,
    secondaryCenters,
    confidence,
    narratorText,
    uiColor: confidence >= 0.7 ? 'red' : confidence >= 0.4 ? 'orange' : 'yellow',
  };
}

// ── Internal helpers ──────────────────────────────────────

interface BBox { minX: number; maxX: number; minY: number; maxY: number }

function cornersBbox(corners: Point2D[], margin: number): BBox {
  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  for (let i = 0; i < corners.length; i++) {
    const c = corners[i];
    if (c.x < minX) minX = c.x;
    if (c.x > maxX) maxX = c.x;
    if (c.y < minY) minY = c.y;
    if (c.y > maxY) maxY = c.y;
  }
  return {
    minX: Math.max(0, minX - margin),
    maxX: Math.min(1, maxX + margin),
    minY: Math.max(0, minY - margin),
    maxY: Math.min(1, maxY + margin),
  };
}

function inBbox(nx: number, ny: number, bbox: BBox): boolean {
  return nx >= bbox.minX && nx <= bbox.maxX && ny >= bbox.minY && ny <= bbox.maxY;
}

interface Cluster {
  cx: number;
  cy: number;
  avgConf: number;
  hits: Array<{ nx: number; ny: number; markerId: number; confidence: number }>;
}

function clusterHits(
  hits: Array<{ nx: number; ny: number; markerId: number; confidence: number }>,
  radius: number,
): Cluster[] {
  const clusters: Cluster[] = [];
  const assigned = new Array<boolean>(hits.length).fill(false);

  for (let i = 0; i < hits.length; i++) {
    if (assigned[i]) continue;
    const members = [hits[i]];
    assigned[i] = true;
    for (let j = i + 1; j < hits.length; j++) {
      if (assigned[j]) continue;
      const dx = hits[i].nx - hits[j].nx;
      const dy = hits[i].ny - hits[j].ny;
      if (Math.sqrt(dx * dx + dy * dy) < radius) {
        members.push(hits[j]);
        assigned[j] = true;
      }
    }
    const cx = members.reduce((s, h) => s + h.nx, 0) / members.length;
    const cy = members.reduce((s, h) => s + h.ny, 0) / members.length;
    const avgConf = members.reduce((s, h) => s + h.confidence, 0) / members.length;
    clusters.push({ cx, cy, avgConf, hits: members });
  }
  return clusters;
}